import React, { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Check, CreditCard, Loader2, Crown, ArrowRight, CheckCircle, XCircle, Clock, RefreshCw } from 'lucide-react';
import { PLANS, getRegionalPricing, detectUserCurrency, formatPrice } from '../utils/pricing';
import { UsageMeters } from '../components/UsageMeters';
import { VoucherRedeemCard } from '../components/VoucherRedeemCard';
import { SubscriptionControls } from '../components/SubscriptionControls';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Load Razorpay script dynamically
const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

export const BillingPage = () => {
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const [loadingPlan, setLoadingPlan] = useState(null); // 'GROWTH' | 'PRO' | null
  const [transactions, setTransactions] = useState([]);
  const { user, refreshUser } = useAuth();

  const currentPlan = user?.subscription?.plan?.toUpperCase() || 'FREE';
  const [currency, setCurrency] = useState('USD');
  const [pricing, setPricing] = useState(getRegionalPricing('USD'));

  useEffect(() => {
    detectUserCurrency().then(c => {
      setCurrency(c);
      setPricing(getRegionalPricing(c));
    });
  }, []);

  const fetchTransactions = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/payments/transactions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTransactions(data);
      }
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
    }
  }, []);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const handleUpgrade = async (planKey) => {
    // Map UI plan key (GROWTH/PRO) → backend internal key (growth/pro)
    const planType = planKey.toLowerCase();
    setLoadingPlan(planKey);

    try {
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        throw new Error('Failed to load payment gateway');
      }

      const token = localStorage.getItem('token');

      // Create Razorpay SUBSCRIPTION (recurring) — Phase 11B self-service flow
      const response = await fetch(`${API_URL}/api/subscriptions/create`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          plan_type: planType,
          interval: billingPeriod,
          total_count: billingPeriod === 'annual' ? 5 : 60,
        }),
      });

      if (!response.ok) {
        let errorMessage = 'Failed to create subscription';
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorMessage;
        } catch (e) {
          errorMessage = `Server error: ${response.status}`;
        }
        throw new Error(errorMessage);
      }

      const sub = await response.json();

      const publicKey = sub.key_id || process.env.REACT_APP_RAZORPAY_KEY_ID || '';

      // If we have a public key, open the inline JS Checkout (preferred — no popup blocker / CSP issues)
      if (publicKey) {
        const options = {
          key: publicKey,
          subscription_id: sub.subscription_id,
          name: 'Formlio',
          description: `${planKey === 'PRO' ? 'Business' : 'Professional'} ${billingPeriod === 'annual' ? 'Annual' : 'Monthly'} subscription`,
          handler: async function () {
            toast.success('Subscription started! Activation typically completes within a minute.');
            await refreshUser();
            fetchTransactions();
            setLoadingPlan(null);
          },
          prefill: {
            name: user?.full_name || '',
            email: user?.email || '',
          },
          theme: { color: '#4F46E5' },
          modal: {
            ondismiss: function () {
              setLoadingPlan(null);
              toast.info('Subscription cancelled');
            },
          },
        };

        const razorpay = new window.Razorpay(options);
        razorpay.on('payment.failed', function (resp) {
          toast.error(`Payment failed: ${resp.error?.description || 'Unknown error'}`);
          setLoadingPlan(null);
        });
        razorpay.open();
        return;
      }

      // Fallback: redirect (NOT window.open — popup blockers + CSP issues) to hosted short_url
      if (sub.short_url) {
        window.location.href = sub.short_url;
        return;
      }

      throw new Error('Payment gateway not configured. Please contact support.');

    } catch (error) {
      console.error('Subscription error:', error);
      toast.error(error.message || 'Failed to initiate subscription');
      setLoadingPlan(null);
    }
  };

  const getStatusBadge = (status, paymentStatus) => {
    if (paymentStatus === 'paid') {
      return <Badge className="bg-green-100 text-green-700"><CheckCircle className="w-3 h-3 mr-1" />Paid</Badge>;
    }
    if (status === 'expired' || paymentStatus === 'expired') {
      return <Badge className="bg-slate-100 text-slate-700"><XCircle className="w-3 h-3 mr-1" />Expired</Badge>;
    }
    return <Badge className="bg-yellow-100 text-yellow-700"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
  };

  const formatAmount = (amount, txCurrency) => {
    const regional = getRegionalPricing(txCurrency || currency);
    return `${regional.symbol}${Number(amount).toFixed(txCurrency === 'INR' ? 0 : 2)}`;
  };

  return (
    <DashboardLayout>
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl md:text-4xl font-heading font-bold text-slate-900" data-testid="billing-title">
            Billing & Subscription
          </h1>
          <p className="text-slate-600 mt-1">Manage your plan and billing information</p>
        </div>

        {/* Current Plan Card */}
        <Card>
          <CardHeader>
            <CardTitle>Current Plan</CardTitle>
            <CardDescription>You are currently on the {PLANS[currentPlan]?.name || 'Free'} plan</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center space-x-4">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  currentPlan === 'PRO' ? 'bg-gradient-to-br from-purple-500 to-indigo-600' :
                  currentPlan === 'GROWTH' ? 'bg-gradient-to-br from-amber-400 to-orange-500' :
                  'bg-slate-200'
                }`}>
                  <Crown className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="font-heading font-semibold text-lg">{PLANS[currentPlan]?.name || 'Free'}</p>
                  <p className="text-slate-600">{PLANS[currentPlan]?.description}</p>
                </div>
              </div>
              <Badge variant="secondary" className="text-green-600 border-green-300 bg-green-50">Active</Badge>
            </div>
            {user?.subscription?.expires_at && (
              <p className="text-sm text-slate-500 mt-3">
                Renews on: {new Date(user.subscription.expires_at).toLocaleDateString()}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Phase 8: Usage meters wired to /api/usage/me */}
        <UsageMeters />

        {/* Phase 11: subscription self-service (Upgrade/Downgrade/Cancel/Reactivate) */}
        <SubscriptionControls />

        {/* Phase 10: voucher redemption */}
        <VoucherRedeemCard />

        {/* Billing Period Toggle */}
        <div className="flex justify-center">
          <div className="inline-flex items-center p-1 bg-slate-100 rounded-full">
            <button
              onClick={() => setBillingPeriod('monthly')}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                billingPeriod === 'monthly'
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingPeriod('annual')}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                billingPeriod === 'annual'
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              Annual
              <span className="ml-2 text-xs text-green-600 font-semibold">Save 20%</span>
            </button>
          </div>
        </div>

        {/* Plans Grid */}
        <div className="grid md:grid-cols-3 gap-6">
          {/* Free Plan */}
          <Card className={currentPlan === 'FREE' ? 'border-2 border-indigo-500 relative' : ''}>
            {currentPlan === 'FREE' && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <Badge className="bg-indigo-600">Current Plan</Badge>
              </div>
            )}
            <CardHeader>
              <CardTitle>🟢 Starter</CardTitle>
              <div className="text-3xl font-heading font-bold text-slate-900 mt-2">
                {pricing.symbol}0
                <span className="text-base text-slate-600 font-normal">/month</span>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-slate-600">For individuals getting started.</p>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Check className="w-4 h-4 text-green-600" />
                  <span className="text-sm text-slate-700">Up to 3 active forms</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Check className="w-4 h-4 text-green-600" />
                  <span className="text-sm text-slate-700">100 submissions/month</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Check className="w-4 h-4 text-green-600" />
                  <span className="text-sm text-slate-700">50 MB storage</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Check className="w-4 h-4 text-green-600" />
                  <span className="text-sm text-slate-700">Up to 100 fields per form</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Check className="w-4 h-4 text-green-600" />
                  <span className="text-sm text-slate-700">5 signed documents/month</span>
                </div>
              </div>
              {currentPlan === 'FREE' ? (
                <Button disabled className="w-full" variant="outline">Current Plan</Button>
              ) : (
                <Button disabled className="w-full" variant="outline">Downgrade</Button>
              )}
            </CardContent>
          </Card>

          {/* Growth Plan */}
          <Card className={`${currentPlan === 'GROWTH' ? 'border-2 border-indigo-500' : ''} relative`}>
            {currentPlan !== 'GROWTH' && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <Badge className="bg-gradient-to-r from-amber-500 to-orange-500">Most Popular</Badge>
              </div>
            )}
            {currentPlan === 'GROWTH' && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <Badge className="bg-indigo-600">Current Plan</Badge>
              </div>
            )}
            <CardHeader>
              <CardTitle>🔵 Professional</CardTitle>
              <div className="text-3xl font-heading font-bold text-slate-900 mt-2">
                {pricing.symbol}{billingPeriod === 'annual' ? pricing.growth.annual : pricing.growth.monthly}
                <span className="text-base text-slate-600 font-normal">/month</span>
              </div>
              {billingPeriod === 'annual' && (
                <p className="text-sm text-green-600">
                  Billed {pricing.symbol}{pricing.growth.annual * 12}/year (Save {pricing.symbol}{(pricing.growth.monthly - pricing.growth.annual) * 12})
                </p>
              )}
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-slate-600">For freelancers and solo creators.</p>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Check className="w-4 h-4 text-green-600" />
                  <span className="text-sm text-slate-700">Up to 25 active forms</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Check className="w-4 h-4 text-green-600" />
                  <span className="text-sm text-slate-700">2,000 submissions/month</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Check className="w-4 h-4 text-green-600" />
                  <span className="text-sm text-slate-700">2 GB storage</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Check className="w-4 h-4 text-green-600" />
                  <span className="text-sm text-slate-700">Up to 250 fields per form</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Check className="w-4 h-4 text-green-600" />
                  <span className="text-sm text-slate-700">50 signed documents/month</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Check className="w-4 h-4 text-green-600" />
                  <span className="text-sm text-slate-700">Custom branding</span>
                </div>
              </div>
              {currentPlan === 'GROWTH' ? (
                <Button disabled className="w-full" variant="outline">Current Plan</Button>
              ) : currentPlan === 'PRO' ? (
                <Button disabled className="w-full" variant="outline">Downgrade</Button>
              ) : (
                <Button
                  className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
                  onClick={() => handleUpgrade('GROWTH')}
                  disabled={loadingPlan !== null}
                  data-testid="upgrade-btn-growth"
                >
                  {loadingPlan === 'GROWTH' ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ArrowRight className="w-4 h-4 mr-2" />}
                  Upgrade to Professional
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Pro Plan */}
          <Card className={currentPlan === 'PRO' ? 'border-2 border-indigo-500 relative' : 'relative'}>
            {currentPlan === 'PRO' && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <Badge className="bg-indigo-600">Current Plan</Badge>
              </div>
            )}
            <CardHeader>
              <CardTitle>🟠 Business</CardTitle>
              <div className="text-3xl font-heading font-bold text-slate-900 mt-2">
                {pricing.symbol}{billingPeriod === 'annual' ? pricing.pro.annual : pricing.pro.monthly}
                <span className="text-base text-slate-600 font-normal">/month</span>
              </div>
              {billingPeriod === 'annual' && (
                <p className="text-sm text-green-600">
                  Billed {pricing.symbol}{pricing.pro.annual * 12}/year (Save {pricing.symbol}{(pricing.pro.monthly - pricing.pro.annual) * 12})
                </p>
              )}
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-slate-600">For growing teams and organizations.</p>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Check className="w-4 h-4 text-green-600" />
                  <span className="text-sm text-slate-700">Up to 100 active forms</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Check className="w-4 h-4 text-green-600" />
                  <span className="text-sm text-slate-700">10,000 submissions/month</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Check className="w-4 h-4 text-green-600" />
                  <span className="text-sm text-slate-700">10 GB storage</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Check className="w-4 h-4 text-green-600" />
                  <span className="text-sm text-slate-700">Up to 500 fields per form</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Check className="w-4 h-4 text-green-600" />
                  <span className="text-sm text-slate-700">500 signed documents/month</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Check className="w-4 h-4 text-green-600" />
                  <span className="text-sm text-slate-700">Remove Formlio branding</span>
                </div>
              </div>
              {currentPlan === 'PRO' ? (
                <Button disabled className="w-full" variant="outline">Current Plan</Button>
              ) : (
                <Button
                  className="w-full bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700"
                  onClick={() => handleUpgrade('PRO')}
                  disabled={loadingPlan !== null}
                  data-testid="upgrade-btn-pro"
                >
                  {loadingPlan === 'PRO' ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ArrowRight className="w-4 h-4 mr-2" />}
                  Upgrade to Business
                </Button>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Enterprise CTA — matches the 4th tier on /pricing */}
        <div
          data-testid="billing-enterprise-cta"
          className="rounded-xl border-2 border-violet-200 bg-gradient-to-br from-violet-50 via-white to-indigo-50 p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
        >
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-violet-100 text-violet-700 flex items-center justify-center shrink-0">
              <Crown className="w-5 h-5" />
            </div>
            <div>
              <p className="font-bold text-slate-900">🟣 Enterprise — unlimited everything</p>
              <p className="text-sm text-slate-600">Custom quotas, SSO, dedicated infra, SLA-backed support. Built around your team.</p>
            </div>
          </div>
          <Button
            onClick={() => window.location.assign('mailto:sales@formlio.app?subject=Formlio%20Enterprise%20Inquiry')}
            data-testid="billing-contact-sales"
            className="bg-slate-900 hover:bg-slate-800 text-white shrink-0"
          >
            Contact Sales
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>

        {/* Payment History */}
        {transactions.length > 0 && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center space-x-2">
                    <CreditCard className="w-5 h-5" />
                    <span>Payment History</span>
                  </CardTitle>
                  <CardDescription>Your recent transactions</CardDescription>
                </div>
                <Button variant="ghost" size="sm" onClick={fetchTransactions}>
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {transactions.map((tx) => (
                  <div key={tx.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div>
                      <p className="font-medium text-slate-900">{tx.plan_name}</p>
                      <p className="text-sm text-slate-500">
                        {new Date(tx.created_at).toLocaleDateString()} at {new Date(tx.created_at).toLocaleTimeString()}
                      </p>
                    </div>
                    <div className="flex items-center space-x-4">
                      <span className="font-semibold">{formatAmount(tx.amount_display || tx.amount, tx.currency)}</span>
                      {getStatusBadge(tx.status, tx.payment_status)}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default BillingPage;
